import { buildElationUrlPatient } from '@seen/core/elation'
import { featureFlags } from '@seen/core/featureFlags'
import * as patients from '@seen/core/models/patients'
import type { Participant } from '@seen/core/models/patients/participant'
import {
  genderOptions,
  getLanguageDisplay,
  type ProfileLanguageCode,
} from '@seen/core/models/profile'
import { DayCenterSchedule } from '@seen/core/models/schedules/dayCenterSchedule'
import {
  Button,
  cn,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Separator,
} from '@seen/ui'
import {
  IconBuilding,
  IconCalendarWeek,
  IconGauge,
  IconLanguage,
  IconLink,
  IconPencil,
  IconUser,
  type TablerIcon,
} from '@tabler/icons-react'
import { format, parseISO } from 'date-fns'
import { compact } from 'lodash-es'
import {
  type ComponentProps,
  type CSSProperties,
  Fragment,
  type JSX,
  type ReactNode,
  useRef,
  useState,
} from 'react'
import { ComboboxButton } from '#src/components/ComboboxButton.tsx'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { CopyButton } from '#src/components/core/CopyButton.tsx'
import { ExternalTextLink } from '#src/components/core/ExternalTextLink.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { Skeleton } from '#src/components/core/Skeleton.tsx'
import { CRMLink } from '#src/components/external-links/CRMLink.tsx'
import { MetriportLink } from '#src/components/external-links/MetriportLink.tsx'
import { useAstranaSearch } from '#src/components/external-links/useAstranaSearch.ts'
import { DevMedplumAppLinkOverlay } from '#src/components/medplum/DevMedplumAppLinkOverlay.tsx'
import { ParticipantFlags } from '#src/components/ParticipantFlags.tsx'
import { EditProfileFieldDialog } from '#src/components/patient/detail-content/contact-tab/EditButton.tsx'
import { LanguagesMultiSelect } from '#src/components/patient/detail-content/contact-tab/LanguagesMultiSelect.tsx'
import { PreferredLanguageStar } from '#src/components/patient/detail-content/contact-tab/PreferredLanguageStar.tsx'
import { DayCenterScheduleDialog } from '#src/components/patient/detail-content/day-center-schedule/DayCenterScheduleDialog.tsx'
import {
  AttendanceDaysInline,
  DayCenterScheduleHoverCard,
} from '#src/components/patient/detail-content/day-center-schedule/DayCenterScheduleSummary.tsx'
import { formatAttendanceDays } from '#src/components/patient/detail-content/day-center-schedule/weekdays.ts'
import { EditableAvatar } from '#src/components/patient/detail-content/EditableAvatar.tsx'
import { DIVIDER_DOT_COLOR_CLASS } from '#src/components/patient/dividerDot.ts'
import { ParticipantRafScoreRow } from '#src/components/patient/ParticipantRafScore.tsx'
import { PatientName } from '#src/components/patient/PatientName.tsx'
import { useConfig } from '#src/contexts/config/useConfig.ts'
import {
  HEADER_HOVER_CARD_WIDTH,
  useAdaptiveHoverCardPlacement,
} from '#src/hooks/useAdaptiveHoverCardPlacement.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useRafScore } from '#src/hooks/useRafScore.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

interface PatientHeaderCardProps {
  participant: Participant
  className?: string
  actions?: ReactNode
  // 'wide' is the Figma "Title Card (Full-Width/Narrow Screen)" layout: the
  // info rows join into one line beside the avatar, pipe-separated. 'stacked'
  // is the detail panel's column layout.
  variant?: 'stacked' | 'wide'
  // False renders a presentation-only card (static rows, no edit
  // affordances) so the card can sit inside another interactive element,
  // e.g. the narrow-screen facesheet sheet trigger.
  interactive?: boolean
}

// Figma Profile Card (1494:26162): #9F0712 border and #FEF2F2 at 40% over
// white (stacked fills). The tint is a CSS variable so `dark:` can swap it;
// the base fill is the theme card color, which is the Figma white in light.
// Dark has no Figma spec: red-950 at 40% over the dark card, and a red-600
// border, since #9F0712 is only 2.4:1 against the dark page.
const CRITICAL_ATTENTION_HEADER_CARD_CLASS_NAME =
  'border-[#9F0712] [--header-card-tint:rgba(254,242,242,0.4)] dark:border-[#E7000B] dark:[--header-card-tint:rgba(70,8,9,0.4)]'
// Figma 2044:28141: Yellow-400 border and yellow-50 at 40% over white. Dark
// keeps the yellow-400 border (11:1 on the dark page) over yellow-950 at 40%.
const ISOLATION_REQUIRED_HEADER_CARD_CLASS_NAME =
  'border-[#FDC700] [--header-card-tint:rgba(254,252,232,0.4)] dark:[--header-card-tint:rgba(67,32,4,0.4)]'
// Inline style so Tailwind cannot drop the commas between the two layers.
const TINTED_HEADER_CARD_STYLE: CSSProperties = {
  backgroundImage:
    'linear-gradient(var(--header-card-tint), var(--header-card-tint)), linear-gradient(hsl(var(--card)), hsl(var(--card)))',
}
// Same ghost-button wash as the more-menu trigger on a tinted card.
const TINTED_HEADER_ROW_HOVER_CLASS_NAME =
  'hover:backdrop-brightness-hover focus-visible:backdrop-brightness-active data-[state=open]:backdrop-brightness-active dark:hover:backdrop-brightness-[1.2]'

// The facesheet participant header. It owns the flag pills. Colors and
// metrics follow the Facesheet Redesign Figma (stone/red palette the app theme
// has not adopted yet, hence the raw hex values).
export function PatientHeaderCard({
  participant,
  className,
  actions,
  variant = 'stacked',
  interactive = true,
}: PatientHeaderCardProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const staticRows = isReadOnly || !interactive
  const wide = variant === 'wide'
  const isCriticalFlagsEnabled = useFeatureFlag(
    featureFlags.facesheetCriticalIsolationFlags
  )
  const [reportedCriticalIsolation, setReportedCriticalIsolation] = useState<
    { criticalAttention: boolean; isolationRequired: boolean } | undefined
  >()
  const showIsolationRequiredCard =
    isCriticalFlagsEnabled &&
    (reportedCriticalIsolation?.isolationRequired ??
      participant.flagCodes.includes(
        patients.ParticipantFlagCode.ISOLATION_REQUIRED
      ))
  const showCriticalAttentionCard =
    isCriticalFlagsEnabled &&
    !showIsolationRequiredCard &&
    (reportedCriticalIsolation?.criticalAttention ??
      participant.flagCodes.includes(
        patients.ParticipantFlagCode.CRITICAL_ATTENTION
      ))
  const showTintedCard = showIsolationRequiredCard || showCriticalAttentionCard
  // Gio's scroll-collapse (Figma 665-2804 / 1353-5964): while the facesheet
  // is scrolled down, the stacked header keeps the identity line and the
  // external-systems links row. Language, day center, and RAF fold away, and
  // the identity row drops its leading icon so that line sits flush under the
  // name. PatientDetailContent owns the scroll signal and publishes it as
  // `data-collapsed` on the group below, so the whole transition is CSS and a
  // scroll gesture re-renders nothing here. The wide bar is a single line
  // already, so it never collapses.
  const collapsible = !wide
  const astranaSearch = useAstranaSearch(participant)
  const config = useConfig()
  // The participant's external-system links, which the header owns rather than
  // the Highlights tab, so they sit in the same place on every tab. Built as a
  // list so the row below can tell whether the participant has any before it
  // commits to a row and its divider. The links carry no type of their own —
  // the row sizes and colors its own text.
  const externalLinks = compact<{ id: string; link: ReactNode }>([
    participant.elationId && {
      id: 'elation',
      link: (
        <ExternalTextLink
          href={buildElationUrlPatient({
            elationBaseUrl: config.elationBaseUrl,
            elationPatientId: participant.elationId,
          })}
        >
          Elation
        </ExternalTextLink>
      ),
    },
    participant.crmUrl && {
      id: 'crm',
      link: <CRMLink participant={participant} />,
    },
    participant.metriportId && {
      id: 'metriport',
      link: <MetriportLink participant={participant} />,
    },
    astranaSearch && {
      id: 'astrana',
      link: (
        // The row lays out its own spacing, so the button contributes no
        // height or padding of its own.
        <Button variant="link" size="unstyled" onClick={astranaSearch}>
          Astrana
        </Button>
      ),
    },
  ])
  // Stacked rows follow the Figma facesheet header with a constant py-1 and the
  // icon pinned to the first line (items-start + mt-0.5), so a wrapped two-line
  // row keeps the same top/bottom padding as a one-line row. Wide rows already
  // stay inline with a first-line icon, unchanged.
  const stackedRowLayoutClasses = 'w-full items-start py-1'
  const rowLayoutClasses = wide ? 'items-start' : stackedRowLayoutClasses
  const rowIconClasses = 'size-3 shrink-0 mt-0.5'
  // Collapsed (Figma 1353-6130): identity loses its icon and extra py, then
  // sits 2px above the links row, which keeps its glyph and centers on the
  // 16px line.
  const collapsedCompactRowClass = cn(
    collapsible &&
      'transition-[padding,margin] duration-200 ease-out motion-reduce:transition-none group-data-[collapsed=true]/facesheet-header:py-0 group-data-[collapsed=true]/facesheet-header:items-center'
  )
  const identityIconClasses = cn(
    rowIconClasses,
    collapsible && COLLAPSING_ROW_ICON_CLASS
  )
  const linksIconClasses = cn(
    rowIconClasses,
    collapsible && 'group-data-[collapsed=true]/facesheet-header:mt-0'
  )
  const rowPencilClasses = cn(
    'ml-auto size-3 shrink-0 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 group-data-[state=open]:opacity-100',
    'mt-0.5'
  )
  // Edit-in-place rows reveal a highlight on hover, focus, or while their
  // popover is open. On the default card that is #fafaf9. On a critical
  // treatment, match the more-menu ghost button: dim the card wash with
  // backdrop-brightness-hover instead of painting an opaque stone fill.
  // has-[>svg]:px-* overrides the button default's has-[>svg]:px-3 so a
  // direct-child icon keeps the same left edge as the other rows. Wide
  // keeps the rounded, 4px-bleed pill (-mx-1 px-1); stacked fills the
  // column with square corners, flush under the row dividers.
  const interactiveRowClasses = cn(
    'justify-start gap-1 p-0 text-left text-xs font-normal leading-4 whitespace-normal',
    showTintedCard
      ? TINTED_HEADER_ROW_HOVER_CLASS_NAME
      : 'hover:bg-[#fafaf9] hover:backdrop-brightness-100 dark:hover:bg-zinc-800',
    rowLayoutClasses,
    wide
      ? 'h-auto min-h-4 -mx-1 px-1 has-[>svg]:px-1 rounded'
      : 'group h-auto has-[>svg]:px-0'
  )
  const [editingBirthDate, setEditingBirthDate] = useState(false)
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false)
  const [detailsHoverOpen, setDetailsHoverOpen] = useState(false)
  const [genderDropdownOpen, setGenderDropdownOpen] = useState(false)
  // isPending keeps the add prompt from flashing before an existing
  // schedule has loaded.
  const { data: schedule, isPending: schedulePending } = useSearchOne(
    DayCenterSchedule,
    DayCenterSchedule.queryByParticipant(participant.reference)
  )
  const { rafScore, isPending: rafPending } = useRafScore(participant.id)
  // The schedule and the RAF score arrive after the card has already painted,
  // so without a placeholder their rows appear late and shove everything below
  // them — including the flag pills — down the page.
  const loadingSkeletons = useFeatureFlag(
    featureFlags.facesheetHeaderLoadingSkeletons
  )
  const attendanceDays = schedule?.attendanceDays ?? []
  const preferredDays = schedule?.preferredAppointmentDays ?? []
  // With the v2 flag on, the schedule row groups attended days by shift
  // ("Mon, Wed [AM] · Thu [PM]") and — when interactive — opens a detail hover
  // card. Off, the row keeps the plain comma-joined days it shows today.
  const showScheduleV2 = useFeatureFlag(featureFlags.dayCenterScheduleV2)
  const wideSeparator = '|'
  // Gio's divider spec: every dot divider in the header becomes the day center
  // schedule row's dot — the same bullet glyph, the same #d6d3d1 stone, and the
  // same 6px of clear space on each side. Each site's spacing class only tops
  // up (or trims back) what its own row's flex gap already contributes, so the
  // measured clear space comes out the same everywhere. The wide bar's pipes
  // are not dots and stay as they are.
  const standardDots = useFeatureFlag(featureFlags.facesheetHeaderDotDividers)
  // Gio's ask: the header's own Participant Details and Day Center Schedule
  // hover cards get the same measured-space placement as the facesheet list
  // cards, so they ride the same flag rather than a header-specific one.
  const headerAdaptivePlacement = useFeatureFlag(
    featureFlags.hoverCardAdaptivePlacement
  )
  const demographicsRowRef = useRef<HTMLSpanElement>(null)
  const {
    side: demographicsAdaptiveSide,
    sideOffset: demographicsAdaptiveSideOffset,
    onOpenChange: demographicsAdaptiveOnOpenChange,
    cardRef: demographicsAdaptiveCardRef,
  } = useAdaptiveHoverCardPlacement(demographicsRowRef, HEADER_HOVER_CARD_WIDTH)
  const scheduleRowRef = useRef<HTMLButtonElement>(null)
  const {
    side: scheduleAdaptiveSide,
    sideOffset: scheduleAdaptiveSideOffset,
    onOpenChange: scheduleAdaptiveOnOpenChange,
    cardRef: scheduleAdaptiveCardRef,
  } = useAdaptiveHoverCardPlacement(scheduleRowRef, HEADER_HOVER_CARD_WIDTH)
  // A day center is a place, so v2 marks the row with a building; the flag-off
  // row keeps the calendar it has always shown.
  const ScheduleIcon = showScheduleV2 ? IconBuilding : IconCalendarWeek
  // Preferred appointment days are independent of attendance, so a schedule can
  // hold preferred days or a note with no center days at all. Show the grouped
  // row + hover whenever any of that content exists — not only for center days
  // — so the preferences stay reachable.
  const hasScheduleContent =
    attendanceDays.length > 0 ||
    preferredDays.length > 0 ||
    Boolean(schedule?.note)
  const scheduleShiftDetail = Boolean(
    showScheduleV2 && schedule && hasScheduleContent
  )
  const scheduleDays =
    scheduleShiftDetail && schedule ? (
      attendanceDays.length > 0 ? (
        <AttendanceDaysInline
          days={attendanceDays}
          shifts={schedule.attendanceShifts}
          // Stacked-only: hide the middots between shift groups when the row
          // wraps (Gio's narrow-card Figma). The wide bar keeps them inline.
          hangingSeparators={!wide}
          standardDots={standardDots}
        />
      ) : (
        // The hover still surfaces the preferred days and note.
        <span className="text-[#a8a29e]">No center days</span>
      )
    ) : attendanceDays.length > 0 ? (
      formatAttendanceDays(attendanceDays)
    ) : (
      <span className="text-[#a8a29e]">Add day center schedule</span>
    )
  // 'MMM d, yyyy' is this header's format; `birthDateFormatted` stays
  // comma-less because the classic header must not change under the flag.
  const birthDateFormatted =
    participant.birthDate &&
    format(parseISO(participant.birthDate), 'MMM d, yyyy')
  // FHIR has no unset gender ('unknown' is the floor), so 'unknown' is what
  // the add prompt replaces — a bare '?' initial says nothing anyway.
  const genderKnown = participant.gender !== 'unknown'
  const demographicsText = compact([
    genderKnown && participant.genderInitial,
    participant.age,
    birthDateFormatted,
  ]).join(', ')
  // Read-only viewers see only the recorded pieces; anyone who can edit keeps
  // both pieces, with a muted add prompt standing in for a missing gender or
  // birth date — the language row's pattern.
  const demographicsInner = isReadOnly ? (
    demographicsText
  ) : (
    <>
      {genderKnown ? (
        participant.genderInitial
      ) : (
        <span className="text-[#a8a29e]">Add gender</span>
      )}
      {', '}
      {participant.birthDate ? (
        `${participant.age}, ${birthDateFormatted}`
      ) : (
        <span className="text-[#a8a29e]">Add birth date</span>
      )}
    </>
  )
  // A single span so the row's flex gap doesn't split the joined text.
  const demographics = isReadOnly ? (
    demographicsInner
  ) : (
    <span className="min-w-0 whitespace-normal">{demographicsInner}</span>
  )
  // The MRN joins the identity row after the demographics as a plain
  // "• MRN <value>" run. Stacked uses the same inline divider as the links
  // row. Wide keeps the hanging/pipe treatment below. The copy affordance
  // lives in the hover card. Rendered only when the participant has one, so
  // no dangling bullet appears otherwise.
  const mrnInline = participant.seenMRN && (
    <>
      <span
        aria-hidden
        className={cn(
          'leading-4',
          // The row's own gap-1 gives 4px a side; mx-0.5 tops both up to 6px.
          standardDots
            ? cn('mx-0.5', DIVIDER_DOT_COLOR_CLASS)
            : 'text-foreground-tertiary'
        )}
      >
        •
      </span>
      <span className="whitespace-nowrap">MRN {participant.seenMRN}</span>
    </>
  )
  // The identity row's contents, shared by its static and interactive forms.
  // Stacked only — the wide bar is single-line by design — the demographics
  // stay on one line (whitespace-nowrap) so that when the MRN can't fit it
  // drops fully below them rather than stacking beside a wrapped demographics
  // block. The MRN bullet is the same HeaderDividerDot the links row uses.
  const identityFields = !wide ? (
    <span className={HEADER_INLINE_ROW_CLASS}>
      <span className="whitespace-nowrap">{demographicsInner}</span>
      {participant.seenMRN && (
        <>
          <HeaderDividerDot />
          <span className="whitespace-nowrap">MRN {participant.seenMRN}</span>
        </>
      )}
    </span>
  ) : (
    <>
      {demographics}
      {mrnInline}
    </>
  )
  // Read-only viewers get no add-flag control, so the flags row (and the
  // separator above it) renders only when there is at least one badge.
  const showFlagsRow =
    !staticRows ||
    participant.flags.length > 0 ||
    participant.isDemo ||
    participant.isOnsite ||
    participant.isDisenrollmentScheduled ||
    participant.isDisenrolled

  // Every row is constant for anyone who can edit: with nothing recorded it
  // shows a muted add prompt instead of vanishing, so the field stays
  // reachable from the card. Read-only viewers get no add affordance, so
  // their empty rows hide (like the flags row).
  const infoRows = compact([
    (demographicsText.length > 0 || !isReadOnly) &&
      (staticRows ? (
        <HeaderInfoRow
          key="demographics"
          icon={IconUser}
          className={cn(rowLayoutClasses, collapsedCompactRowClass)}
          iconClassName={identityIconClasses}
        >
          {identityFields}
        </HeaderInfoRow>
      ) : (
        <HoverCard
          key="demographics"
          openDelay={200}
          // Held open while the inline gender dropdown is open: the dropdown
          // portals outside the hover card, so pointer-out would otherwise
          // dismiss the hover card (and the dropdown with it).
          open={detailsHoverOpen || genderDropdownOpen}
          onOpenChange={(open) => {
            setDetailsHoverOpen(open)
            if (headerAdaptivePlacement) {
              demographicsAdaptiveOnOpenChange(open)
            }
          }}
        >
          <HoverCardTrigger asChild>
            <HeaderInfoRow
              ref={demographicsRowRef}
              icon={IconUser}
              className={cn(
                wide ? rowLayoutClasses : interactiveRowClasses,
                !wide && collapsedCompactRowClass
              )}
              iconClassName={identityIconClasses}
            >
              {identityFields}
            </HeaderInfoRow>
          </HoverCardTrigger>
          <HoverCardContent
            ref={
              headerAdaptivePlacement ? demographicsAdaptiveCardRef : undefined
            }
            // These rows are full-width by design (the hover highlight spans
            // the row), so a right-side card anchors off the header's far edge
            // and Radix can only flip it, never slide it sideways. Bottom is
            // the flag-off legacy placement for that reason; adaptive
            // placement (headerAdaptivePlacement) sidesteps it by sliding the
            // card in from the measured row/viewport space instead, the same
            // way the facesheet list cards do.
            side={headerAdaptivePlacement ? demographicsAdaptiveSide : 'bottom'}
            sideOffset={
              headerAdaptivePlacement
                ? demographicsAdaptiveSideOffset
                : undefined
            }
            align="start"
            scrollChainAnchor={
              headerAdaptivePlacement ? demographicsRowRef : undefined
            }
            className="flex w-80 flex-col gap-2"
          >
            <p className="text-base leading-6 font-semibold tracking-[-0.53px] text-foreground">
              Participant Details
            </p>
            <ParticipantDetailRow label="Gender">
              <ResourceFieldForm
                instance={participant}
                valueFn={(profile) => profile.gender}
                patchFn={(profile, value) => {
                  profile.gender = value
                }}
                render={({ field }) => (
                  <ComboboxButton
                    options={genderOptions}
                    variant="underline"
                    className="h-5 p-0 text-xs font-normal text-foreground-secondary"
                    onOpenChange={setGenderDropdownOpen}
                    {...field}
                  />
                )}
              />
            </ParticipantDetailRow>
            <Separator />
            <ParticipantDetailRow
              label="Birth Date"
              editLabel="Edit birth date"
              onEdit={() => setEditingBirthDate(true)}
            >
              {participant.birthDate ? (
                <>
                  {birthDateFormatted}
                  {/* The one divider here that isn't already a bullet: off, a
                      narrower middot in the darker tertiary; on, the header's
                      standard dot, the row's gap-2 trimmed to the same 6px. */}
                  <span
                    className={
                      standardDots
                        ? cn('-mx-0.5', DIVIDER_DOT_COLOR_CLASS)
                        : 'text-foreground-tertiary'
                    }
                  >
                    {standardDots ? '•' : '·'}
                  </span>
                  {participant.age}
                </>
              ) : (
                'Unknown'
              )}
            </ParticipantDetailRow>
            {participant.seenMRN && (
              <>
                <Separator />
                <ParticipantDetailRow label="MRN">
                  {participant.seenMRN}
                  <CopyButton
                    value={participant.seenMRN}
                    ariaLabel="Copy MRN"
                    className="-m-0.5 size-auto p-0.5 [&_svg]:size-3"
                  />
                </ParticipantDetailRow>
              </>
            )}
          </HoverCardContent>
        </HoverCard>
      )),
    // The language row's contents mirror the profile's languages — the same
    // source the Contact tab's Languages section edits, so changes there
    // flow back here.
    (participant.languageCodes.length > 0 || !isReadOnly) &&
      (staticRows ? (
        <HeaderInfoRow
          key="language"
          icon={IconLanguage}
          className={rowLayoutClasses}
          iconClassName={rowIconClasses}
        >
          {participant.languageCodes.length > 0 ? (
            <LanguagesInline languages={participant.languageCodes} />
          ) : (
            <span className="text-[#a8a29e]">Add language</span>
          )}
        </HeaderInfoRow>
      ) : (
        <LanguagesMultiSelect
          key="language"
          profile={participant}
          variant="ghost"
          className={interactiveRowClasses}
          renderTrigger={(value) => (
            <span
              className={cn(
                'flex min-w-0 gap-1',
                wide ? 'items-start' : 'w-full items-start'
              )}
            >
              <IconLanguage className={rowIconClasses} />
              {value.length > 0 ? (
                <LanguagesInline languages={value} />
              ) : (
                <span className="min-w-0 whitespace-normal text-[#a8a29e]">
                  Add language
                </span>
              )}
              <IconPencil className={rowPencilClasses} />
            </span>
          )}
        />
      )),
    loadingSkeletons && schedulePending ? (
      <HeaderRowSkeleton
        key="schedule"
        icon={ScheduleIcon}
        className={rowLayoutClasses}
        iconClassName={rowIconClasses}
        barClassName="w-24"
        testId="schedule-row-loading"
      />
    ) : (
      (attendanceDays.length > 0 || (!isReadOnly && !schedulePending)) &&
      (staticRows ? (
        <HeaderInfoRow
          key="schedule"
          icon={ScheduleIcon}
          className={rowLayoutClasses}
          iconClassName={rowIconClasses}
        >
          {scheduleDays}
        </HeaderInfoRow>
      ) : scheduleShiftDetail && schedule ? (
        // Hovering the row reveals the full schedule (shifts, preferred
        // external-appointment days, note). The hover card carries the edit
        // pencil, so the row itself shows none; clicking the row still opens
        // the edit dialog, keeping a keyboard path in.
        <HoverCard
          key="schedule"
          openDelay={200}
          onOpenChange={
            headerAdaptivePlacement ? scheduleAdaptiveOnOpenChange : undefined
          }
        >
          <HoverCardTrigger asChild>
            <Button
              ref={scheduleRowRef}
              variant="ghost"
              onClick={() => setScheduleDialogOpen(true)}
              className={cn('group', interactiveRowClasses)}
            >
              <ScheduleIcon className={rowIconClasses} />
              {scheduleDays}
            </Button>
          </HoverCardTrigger>
          <HoverCardContent
            ref={headerAdaptivePlacement ? scheduleAdaptiveCardRef : undefined}
            side={headerAdaptivePlacement ? scheduleAdaptiveSide : 'bottom'}
            sideOffset={
              headerAdaptivePlacement ? scheduleAdaptiveSideOffset : undefined
            }
            align="start"
            scrollChainAnchor={
              headerAdaptivePlacement ? scheduleRowRef : undefined
            }
            className="w-80"
          >
            <DayCenterScheduleHoverCard
              schedule={schedule}
              onEdit={() => setScheduleDialogOpen(true)}
            />
          </HoverCardContent>
        </HoverCard>
      ) : (
        <Button
          key="schedule"
          variant="ghost"
          onClick={() => setScheduleDialogOpen(true)}
          className={cn('group', interactiveRowClasses)}
        >
          <ScheduleIcon className={rowIconClasses} />
          {scheduleDays}
          <IconPencil className={rowPencilClasses} />
        </Button>
      ))
    ),
    loadingSkeletons && rafPending ? (
      <HeaderRowSkeleton
        key="raf-score"
        icon={IconGauge}
        className={rowLayoutClasses}
        iconClassName={rowIconClasses}
        barClassName="w-16"
        testId="raf-score-row-loading"
      />
    ) : (
      rafScore && (
        <ParticipantRafScoreRow
          key="raf-score"
          rafScore={rafScore}
          patientId={interactive ? participant.id : undefined}
          className={rowLayoutClasses}
          iconClassName={rowIconClasses}
        />
      )
    ),
    // The presentation-only card is excluded because it fills the
    // narrow-screen sheet's trigger, where an anchor would nest inside a
    // button. Read-only viewers keep the row: following a link changes nothing.
    interactive && externalLinks.length > 0 && (
      <HeaderInfoRow
        key="links"
        icon={IconLink}
        className={cn(rowLayoutClasses, collapsedCompactRowClass)}
        iconClassName={linksIconClasses}
      >
        <span className={HEADER_INLINE_ROW_CLASS}>
          {externalLinks.map(({ id, link }, index) => (
            <Fragment key={id}>
              {index > 0 && <HeaderDividerDot />}
              {link}
            </Fragment>
          ))}
        </span>
      </HeaderInfoRow>
    ),
  ])
  // Identity is the demographics row, not "whatever is first". A read-only
  // sparse profile can omit demographics/language/schedule/RAF and leave
  // links as infoRows[0]; treating that as identity would emit it twice.
  const identityRow = infoRows.find((row) => row.key === 'demographics')
  const linksRow = infoRows.find((row) => row.key === 'links')
  const foldableRows = infoRows.filter(
    (row) => row.key !== 'demographics' && row.key !== 'links'
  )

  return (
    <div
      className={cn(
        'flex flex-col rounded-lg border p-3 text-zinc-950 dark:text-zinc-50',
        showIsolationRequiredCard
          ? ISOLATION_REQUIRED_HEADER_CARD_CLASS_NAME
          : showCriticalAttentionCard
            ? CRITICAL_ATTENTION_HEADER_CARD_CLASS_NAME
            : 'border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950',
        className
      )}
      style={showTintedCard ? TINTED_HEADER_CARD_STYLE : undefined}
      data-critical-attention-card={
        showCriticalAttentionCard ? 'true' : undefined
      }
      data-isolation-required-card={
        showIsolationRequiredCard ? 'true' : undefined
      }
    >
      <div className={cn('flex gap-3', wide ? 'items-center' : 'items-start')}>
        {interactive ? (
          <EditableAvatar
            profile={participant.toFhirResource()}
            className="border border-zinc-200 dark:border-zinc-800"
          />
        ) : (
          <Avatar
            profile={participant.toFhirResource()}
            className="border border-zinc-200 dark:border-zinc-800"
          />
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm leading-5 font-semibold tracking-[-0.01em]">
              <PatientName
                participant={participant}
                name={participant.nameLastFirstMiddleInitial}
                nicknameClassName="text-foreground-secondary"
              />
              <DevMedplumAppLinkOverlay to={participant} inline />
            </p>
            {actions}
          </div>
          <div
            className={cn(
              'text-foreground-secondary text-xs tracking-[-0.01em]',
              wide
                ? 'mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5'
                : 'mt-1 flex flex-col group-data-[collapsed=true]/facesheet-header:gap-0.5'
            )}
          >
            {wide
              ? infoRows.flatMap((row, index) =>
                  index === 0
                    ? [row]
                    : [
                        <span
                          key={`separator-${row.key}`}
                          aria-hidden
                          className={cn(
                            'leading-4',
                            // Only the bullet form standardizes — the bar's own
                            // gap-x-2 gives 8px a side, which -mx-0.5 trims to
                            // 6px. Pipes keep the wider spacing they were
                            // designed with.
                            'text-foreground-tertiary'
                          )}
                        >
                          {wideSeparator}
                        </span>,
                        row,
                      ]
                )
              : // Stacked: identity stays put. Language / schedule / RAF fold
                // away on collapse (`hidden`, so they cannot add gap). The
                // links row stays under identity (Figma 1353-6130) 2px below.
                [
                  identityRow,
                  foldableRows.length > 0 && (
                    <div
                      key="collapsible-rows"
                      data-testid="header-collapsible-rows"
                      className={COLLAPSIBLE_ROWS_CLASS}
                    >
                      <div className="flex min-h-0 flex-col overflow-hidden">
                        {stackedRowsBelowIdentity(
                          foldableRows,
                          identityRow != null
                        )}
                      </div>
                    </div>
                  ),
                  linksRow && [
                    (identityRow != null || foldableRows.length > 0) && (
                      <div
                        key="divider-links"
                        aria-hidden
                        data-testid="header-row-divider"
                        className="h-px w-full bg-border group-data-[collapsed=true]/facesheet-header:hidden"
                      />
                    ),
                    linksRow,
                  ],
                ]}
          </div>
        </div>
      </div>
      {showFlagsRow && (
        <>
          {/* The divider→badges gap is 10px and the gap above the divider is
              8px (Figma node 536-1799). */}
          <Separator
            className={cn(
              'mb-2.5 mt-2',
              collapsible &&
                'group-data-[collapsed=true]/facesheet-header:my-2.5'
            )}
          />
          <ParticipantFlags
            participant={participant}
            editable={!staticRows}
            variant="header"
            onCriticalIsolationActiveChange={setReportedCriticalIsolation}
          />
        </>
      )}
      {!staticRows && (
        <>
          <EditProfileFieldDialog
            profile={participant.toFhirResource()}
            field="birthDate"
            open={editingBirthDate}
            onOpenChange={(open) => {
              if (!open) {
                setEditingBirthDate(false)
              }
            }}
          />
          <DayCenterScheduleDialog
            open={scheduleDialogOpen}
            onOpenChange={setScheduleDialogOpen}
            participant={participant}
            schedule={schedule ?? null}
          />
        </>
      )}
    </div>
  )
}

// Scroll-collapse (Figma 665-2804). grid-template-rows animates the fold
// without measuring it. Collapsed, `hidden` drops it from flex flow so it
// cannot add space between identity and links (Figma 1353:6130 is 2px).
const COLLAPSIBLE_ROWS_CLASS =
  'grid visible overflow-hidden min-h-0 grid-rows-[1fr] opacity-100 transition-[grid-template-rows,opacity,visibility] duration-200 ease-out motion-reduce:transition-none group-data-[collapsed=true]/facesheet-header:grid-rows-[0fr] group-data-[collapsed=true]/facesheet-header:opacity-0 group-data-[collapsed=true]/facesheet-header:invisible group-data-[collapsed=true]/facesheet-header:hidden'

// The collapsed identity line has no leading icon, so it sits flush under the
// name. size-0 drops width and height; the negative margin eats the row's 4px
// gap as the icon shrinks.
const COLLAPSING_ROW_ICON_CLASS =
  'transition-[width,height,opacity,margin] duration-200 ease-out motion-reduce:transition-none group-data-[collapsed=true]/facesheet-header:size-0 group-data-[collapsed=true]/facesheet-header:opacity-0 group-data-[collapsed=true]/facesheet-header:mt-0 group-data-[collapsed=true]/facesheet-header:-mr-1'

// Hairlines between stacked rows below identity. `leadingDivider` is the
// line above the first foldable row; skip it when identity itself is absent
// so a language/schedule/RAF-only stack does not start with a stray rule.
function stackedRowsBelowIdentity(
  rowsBelow: JSX.Element[],
  leadingDivider = true
): JSX.Element[] {
  return rowsBelow.flatMap((row, index) => [
    ...(leadingDivider || index > 0
      ? [
          <div
            key={`divider-${row.key}`}
            aria-hidden
            data-testid="header-row-divider"
            className="h-px w-full bg-border"
          />,
        ]
      : []),
    row,
  ])
}

// All of the profile's languages, not just the preferred one, so edits made
// through the language row are visible on the card itself (mirrors the
// Contact tab's Languages section). Wraps to multiple lines rather than
// truncating.
function LanguagesInline({
  languages,
}: {
  languages: ProfileLanguageCode[]
}): JSX.Element {
  return (
    <span className="min-w-0 whitespace-normal">
      {languages.map(({ code, preferred }, index) => (
        // Codes repeat across entries in imported data, so the index has to
        // carry the key.
        <span key={`${code}-${index}`}>
          {getLanguageDisplay(code)}
          {preferred && <PreferredLanguageStar />}
          {index < languages.length - 1 ? ', ' : ''}
        </span>
      ))}
    </span>
  )
}

function ParticipantDetailRow({
  label,
  editLabel,
  onEdit,
  children,
}: {
  label: string
  editLabel?: string
  onEdit?: () => void
  children: ReactNode
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2 text-xs leading-4">
      <span className="text-foreground">{label}</span>
      <span className="flex items-center gap-2 text-foreground-secondary">
        {children}
        {onEdit && (
          <Button
            variant="ghost"
            size="unstyled"
            aria-label={editLabel}
            className="-m-0.5 rounded p-0.5"
            onClick={onEdit}
          >
            <IconPencil className="size-3" />
          </Button>
        )}
      </span>
    </div>
  )
}

// Inline identity/links items: 6px between each field and the stone bullet
// that divides them (Figma 1353:6146). Shared so the MRN bullet cannot drift
// from the Elation/Metriport bullet.
const HEADER_INLINE_ROW_CLASS =
  'flex flex-wrap gap-y-0.5 gap-x-1.5 items-center min-w-0'

// A row whose value is still in flight. The bar is the row's 16px line box
// (h-3 + my-0.5) so the finished row replaces it without moving anything, and
// `barClassName` sets a width close to the value the row usually shows.
function HeaderRowSkeleton({
  icon: Icon,
  iconClassName,
  className,
  barClassName,
  testId,
}: {
  icon: TablerIcon
  iconClassName?: string
  className?: string
  barClassName?: string
  testId: string
}): JSX.Element {
  return (
    <span
      aria-hidden
      data-testid={testId}
      className={cn('flex gap-1 leading-4', className)}
    >
      <Icon className={cn('size-3 shrink-0 opacity-50', iconClassName)} />
      <Skeleton className={cn('my-0.5 h-3', barClassName)} />
    </span>
  )
}

function HeaderDividerDot(): JSX.Element {
  return (
    <span aria-hidden className={cn('shrink-0', DIVIDER_DOT_COLOR_CLASS)}>
      •
    </span>
  )
}

// Spreads the rest props (including ref) so it can anchor a Radix trigger
// via asChild. The caller supplies the row layout (height, alignment) and the
// icon's alignment through className/iconClassName so wide and stacked rows
// share one row body.
function HeaderInfoRow({
  icon: Icon,
  iconClassName,
  className,
  children,
  ...delegated
}: ComponentProps<'span'> & {
  icon: TablerIcon
  iconClassName?: string
}): JSX.Element {
  return (
    <span className={cn('flex gap-1 leading-4', className)} {...delegated}>
      <Icon className={cn('size-3 shrink-0', iconClassName)} />
      {children}
    </span>
  )
}
